import { useCallback, useState } from "react";
import { fetchPublishCreativeList } from "../apiService";
import { PublishAdCreative } from "../types";

const PAGE_SIZE = 20;

type PublishState = "published" | "unpublished";

interface UsePublishPaginationResult {
  data: PublishAdCreative[];
  setData: React.Dispatch<React.SetStateAction<PublishAdCreative[]>>;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  fetchData: (cursor?: string | null, append?: boolean) => Promise<void>;
  handleLoadMore: () => void;
}

export function usePublishPagination(
  accountId: string | undefined,
  state: PublishState,
): UsePublishPaginationResult {
  const [data, setData] = useState<PublishAdCreative[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const fetchData = useCallback(
    async (fetchCursor?: string | null, append = false) => {
      if (!accountId) return;
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        const res = await fetchPublishCreativeList(accountId, {
          state,
          page_size: PAGE_SIZE,
          cursor: fetchCursor ?? undefined,
        });
        if (append) {
          setData((prev) => [...prev, ...res.data]);
        } else {
          setData(res.data);
        }
        setCursor(res.pagination.next_cursor);
        setHasMore(res.pagination.has_more);
      } catch (error) {
        console.error(`Failed to fetch ${state} data:`, error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [accountId, state],
  );

  const handleLoadMore = useCallback(() => {
    if (hasMore && !loadingMore && cursor) {
      void fetchData(cursor, true);
    }
  }, [hasMore, loadingMore, cursor, fetchData]);

  return {
    data,
    setData,
    loading,
    loadingMore,
    hasMore,
    fetchData,
    handleLoadMore,
  };
}
